import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { BotControllerApi } from 'api/controllers/bot-controller-api';
import { CartUserAPIApi } from 'api/controllers/cart-user-apiapi';
import { useTelegramUser } from 'hooks/useTelegramUser';
import { QueryKeys } from 'utils/queryKeys';

type SubmitArgs = {
  items: { menuItemId: number; quantity: number }[];
  comment?: string;
  existingOrderId?: number;
};

export const useApi = () => {
  const { dayDate } = useParams<{ dayDate: string }>();
  const { id: userId } = useTelegramUser();

  const getOrder = useQuery(
    [QueryKeys.GET_ORDER_BY_DAY, dayDate, userId],
    () =>
      BotControllerApi.getOrder_next({
        telegramId: userId as number,
        localDate: dayDate as string,
      }),
    { enabled: !!userId },
  );

  // Next-stack ordering works off the server-side cart: sync the client cart, then place/update.
  // Combos are ordered as-is (no per-order modifications yet) — they carry a real menuItemId.
  const submitOrder = useMutation(
    async ({ items, comment, existingOrderId }: SubmitArgs) => {
      const date = dayDate as string;
      await CartUserAPIApi.clearCart({ date });
      for (const item of items) {
        await CartUserAPIApi.addItemToCart({
          date,
          addCartItemRequest: {
            menuItemId: item.menuItemId,
            quantity: item.quantity,
          },
        });
      }
      await CartUserAPIApi.updateCartGeneralComment({
        date,
        updateCartCommentRequest: { comment },
      });
      return existingOrderId
        ? BotControllerApi.updateAnExistingOrder_next({ orderDate: date })
        : BotControllerApi.createNewOrder_next({ orderDate: date });
    },
  );

  const deleteOrder = useMutation(BotControllerApi.deleteOrder);

  return { getOrder, submitOrder, deleteOrder };
};
